import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspaceResolver } from '@/hooks/useWorkspaceResolver';
import { useAuth } from '@/lib/auth';
import { Deal } from '@/hooks/useDeals';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';
import { Loader2, RotateCcw } from 'lucide-react';

const REOPEN_STAGES: { value: Deal['stage']; label: string }[] = [
  { value: 'qualified', label: 'Qualified' },
  { value: 'proposal_sent', label: 'Proposal Sent' },
  { value: 'negotiation', label: 'Negotiation' },
  { value: 'new', label: 'New' },
];

interface ReopenDealModalProps {
  deal: Deal;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ReopenDealModal({ deal, open, onOpenChange }: ReopenDealModalProps) {
  const { workspaceId } = useWorkspaceResolver();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  const [newStage, setNewStage] = useState<Deal['stage']>('qualified');

  const reopenMutation = useMutation({
    mutationFn: async () => {
      // Update deal using type assertion for strict Supabase types
      const { data, error } = await supabase
        .from('deals')
        .update({ 
          stage: newStage,
          closed_at: null,
        } as any)
        .eq('id', deal.id as any)
        .select('id, lead_id')
        .single();

      if (error) throw error;
      
      const leadId = (data as any)?.lead_id;

      // Update lead pipeline stage
      if (leadId) {
        type LeadPipelineStage = 'new' | 'contacted' | 'appointment' | 'proposal' | 'won' | 'lost';
        const leadStageMap: Record<string, LeadPipelineStage> = {
          new: 'new',
          qualified: 'contacted',
          proposal_sent: 'proposal',
          negotiation: 'proposal',
        };
        const mappedStage: LeadPipelineStage = leadStageMap[newStage] || 'contacted';
        await supabase
          .from('leads')
          .update({ 
            pipeline_stage: mappedStage,
          } as any)
          .eq('id', leadId as any);
      }

      // Log the reopen event
      if (workspaceId && user?.id && leadId) {
        await supabase.from('action_events').insert({
          workspace_id: workspaceId,
          user_id: user.id,
          lead_id: leadId,
          action_type: 'stage_change',
          outcome: null,
          meta: { 
            deal_id: deal.id, 
            previous_stage: deal.stage,
            new_stage: newStage,
            action: 'reopened',
          },
        } as any);
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deals', workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['leads', workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['revenue-analytics', workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats', workspaceId] });
      
      const stageLabel = REOPEN_STAGES.find(s => s.value === newStage)?.label || newStage;
      toast({ 
        title: 'Deal Reopened', 
        description: `${deal.lead?.name || 'Deal'} moved to ${stageLabel}` 
      });
      
      onOpenChange(false);
    },
    onError: (error) => {
      console.error('REOPEN_DEAL_ERROR', error);
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    reopenMutation.mutate();
  };

  const previousStatus = deal.stage === 'won' ? 'Won' : 'Lost';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="w-5 h-5 text-primary" />
            Reopen Deal
          </DialogTitle>
          <DialogDescription>
            Move this {previousStatus.toLowerCase()} deal back into the active pipeline.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="p-4 bg-muted/50 border rounded-lg">
            <p className="font-medium">{deal.lead?.name || 'Deal'}</p>
            {deal.title && (
              <p className="text-sm text-muted-foreground">{deal.title}</p>
            )}
            <p className="text-xs text-muted-foreground mt-1">
              Currently: <span className={deal.stage === 'won' ? 'text-success' : 'text-destructive'}>{previousStatus}</span>
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="newStage">Move to Stage</Label>
            <Select value={newStage} onValueChange={(v) => setNewStage(v as Deal['stage'])}>
              <SelectTrigger>
                <SelectValue placeholder="Select stage" />
              </SelectTrigger>
              <SelectContent>
                {REOPEN_STAGES.map(stage => (
                  <SelectItem key={stage.value} value={stage.value}>
                    {stage.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Historical data (won/lost reason) will be preserved.
            </p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={reopenMutation.isPending}
            >
              {reopenMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <RotateCcw className="w-4 h-4 mr-2" />
              Reopen Deal
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
